import { useMemo, useCallback } from "react";
import { useFlags } from "@unleash/proxy-client-react";
import { getFeatureFlag } from "@/config/datasources";

/**
 * Normalize flag names for comparison: lowercase, strip diacritics, spaces, underscores, and dashes.
 */
const normalize = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\s_-]/g, "");

/**
 * Hook that provides feature flag checking for datasource flags.
 * Builds a normalized flag map from the Unleash client SDK flags.
 *
 * Returns:
 * - `isFeatureEnabled(flagName)` - check if a specific flag name is enabled
 * - `isSourceEnabled(sourceKey)` - check if a datasource is enabled (no flag = always enabled)
 * - `getEnabledSourceNames()` - get list of all enabled datasource enum keys
 */
const useSourceFeatureFlags = () => {
  const flags = useFlags();

  const normalizedFlags = useMemo(() => {
    const map = new Map<string, boolean>();

    const setEnabled = (name: string, val: unknown) => {
      let enabled = false;
      if (typeof val === "boolean") {
        enabled = val;
      } else if (val && typeof val === "object") {
        const obj = val as {
          enabled?: boolean;
          variant?: {
            enabled?: boolean;
            featureEnabled?: boolean;
            feature_enabled?: boolean;
          };
        };
        enabled = Boolean(
          obj.enabled ??
            obj.variant?.enabled ??
            obj.variant?.featureEnabled ??
            obj.variant?.feature_enabled
        );
      }
      map.set(normalize(name), enabled);
    };

    if (Array.isArray(flags)) {
      for (const f of flags) {
        if (!f || typeof f !== "object" || !("name" in f)) continue;
        const { name, enabled, variant } = f as {
          name: string;
          enabled?: boolean;
          variant?: {
            enabled?: boolean;
            featureEnabled?: boolean;
            feature_enabled?: boolean;
          };
        };
        setEnabled(name, { enabled, variant });
      }
    } else if (flags && typeof flags === "object") {
      Object.entries(flags as Record<string, unknown>).forEach(([name, val]) =>
        setEnabled(name, val)
      );
    }

    return map;
  }, [flags]);

  const isFeatureEnabled = (name: string | null | undefined): boolean => {
    if (!name) return false;
    return normalizedFlags.get(normalize(name)) === true;
  };

  /**
   * Check if a datasource is enabled based on its feature flag.
   * Sources without a feature flag are always enabled.
   */
  const isSourceEnabled = (sourceKey: string): boolean => {
    const flagName = getFeatureFlag(sourceKey);
    if (flagName === null) return true;
    return isFeatureEnabled(flagName);
  };

  /**
   * Get the raw Unleash flag names of all enabled flags.
   * Returns the original flag names (e.g., "Kilde-Eurlex", "PII-Redaction")
   * which are already ASCII-safe and can be sent directly as an HTTP header value.
   */
  const getEnabledFlagNames = useCallback((): string[] => {
    const enabled: string[] = [];

    if (Array.isArray(flags)) {
      for (const f of flags) {
        if (!f || typeof f !== "object" || !("name" in f)) continue;
        const flag = f as { name: string; enabled?: boolean };
        if (flag.enabled) {
          enabled.push(flag.name);
        }
      }
    } else if (flags && typeof flags === "object") {
      for (const [name, val] of Object.entries(flags as Record<string, unknown>)) {
        if (val && typeof val === "object" && (val as { enabled?: boolean }).enabled) {
          enabled.push(name);
        } else if (val === true) {
          enabled.push(name);
        }
      }
    }

    return enabled;
  }, [flags]);

  return { isFeatureEnabled, isSourceEnabled, getEnabledFlagNames, normalizedFlags };
};

export default useSourceFeatureFlags;
